/**
 * Ligar e desligar os avisos no celular, do lado do navegador.
 *
 * São três coisas separadas que a tela costuma confundir numa só: a permissão
 * do navegador, a inscrição no serviço de push, e a linha no banco da Ruth.
 * Este arquivo mantém as três em fila — pede permissão, inscreve, avisa o
 * servidor — e devolve um estado que a tela consegue explicar em uma frase.
 *
 * **A permissão negada não volta pelo app.** Depois que a mãe toca em
 * "Bloquear", `Notification.requestPermission()` responde "denied" na hora,
 * para sempre, sem mostrar nada. Só as configurações do navegador desfazem. A
 * tela precisa dizer isso; um botão que não faz nada é pior que um botão que
 * não existe.
 *
 * **No iPhone só funciona com o app adicionado à tela inicial (iOS 16.4+).**
 * Fora da tela inicial o Safari nem oferece `PushManager`, e insistir seria
 * mentir para ela.
 */

import { api } from './api'

export type Permissao = 'concedida' | 'negada' | 'nao_perguntada'

export type EstadoDosAvisos = {
  /** O navegador tem service worker, push e notificação. */
  suportado: boolean
  permissao: Permissao
  /** Já existe inscrição neste aparelho. É isto que a chave da tela reflete. */
  ligado: boolean
  /** iPhone fora da tela inicial: nem adianta pedir. */
  faltaInstalarNoIphone: boolean
}

/** Quanto se espera pelo service worker antes de desistir com uma frase. */
const ESPERA_PELO_SW = 10_000

export function suportado(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

function ehIphone(): boolean {
  const agente = navigator.userAgent
  if (/iPhone|iPad|iPod/i.test(agente)) return true
  // O iPad de iPadOS 13 em diante se apresenta como Mac. O que o entrega é ter
  // toque: nenhum Mac de verdade tem.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

/** O app aberto pelo ícone da tela inicial, e não por dentro do navegador. */
function naTelaInicial(): boolean {
  const comoIosChama = (window.navigator as Navigator & { standalone?: boolean }).standalone
  return window.matchMedia('(display-mode: standalone)').matches || comoIosChama === true
}

function comoEstaAPermissao(): Permissao {
  if (!('Notification' in window)) return 'nao_perguntada'
  if (Notification.permission === 'granted') return 'concedida'
  if (Notification.permission === 'denied') return 'negada'
  return 'nao_perguntada'
}

export async function estado(): Promise<EstadoDosAvisos> {
  const faltaInstalarNoIphone = ehIphone() && !naTelaInicial()

  if (!suportado()) {
    return { suportado: false, permissao: 'nao_perguntada', ligado: false, faltaInstalarNoIphone }
  }

  // `getRegistration` e não `ready`: aqui só se lê o que já existe, e `ready`
  // fica esperando para sempre quando não há service worker nenhum.
  const registro = await navigator.serviceWorker.getRegistration()
  const inscricao = registro ? await registro.pushManager.getSubscription() : null

  return {
    suportado: true,
    permissao: comoEstaAPermissao(),
    ligado: Boolean(inscricao),
    faltaInstalarNoIphone,
  }
}

/**
 * A chave pública do servidor, em bytes.
 *
 * `pushManager.subscribe` quer os 65 bytes crus do ponto da curva; o servidor
 * manda em base64url, que é o alfabeto do JSON e da URL. A conversão precisa
 * repor o `=` que o base64url corta e desfazer as trocas de `-` e `_`; sem
 * isso o `atob` recusa a chave e a inscrição falha sem explicação.
 */
function chaveEmBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const falta = (4 - (base64url.length % 4)) % 4
  const base64 = (base64url + '='.repeat(falta)).replace(/-/g, '+').replace(/_/g, '/')
  const bruto = window.atob(base64)
  // O buffer vem explícito para o tipo ser `ArrayBuffer` e não
  // `ArrayBufferLike`: `applicationServerKey` não aceita memória
  // compartilhada, e sem isto o TypeScript recusa a chamada.
  const bytes = new Uint8Array(new ArrayBuffer(bruto.length))
  for (let i = 0; i < bruto.length; i += 1) bytes[i] = bruto.charCodeAt(i)
  return bytes
}

async function servicoPronto(): Promise<ServiceWorkerRegistration> {
  const espera = new Promise<never>((_, recusar) =>
    setTimeout(() => recusar(new Error('não consegui preparar os avisos neste aparelho')), ESPERA_PELO_SW),
  )
  return Promise.race([navigator.serviceWorker.ready, espera])
}

/**
 * Inscreve, e resolve o caso de já existir inscrição com outra chave.
 *
 * Quando o par VAPID do servidor é trocado, o navegador recusa assinar por
 * cima da inscrição antiga: ele levanta em vez de substituir. Sair da antiga e
 * entrar de novo é o único caminho — e é isto que faz uma troca de chave no
 * servidor não deixar a mãe permanentemente sem aviso.
 */
async function assinar(registro: ServiceWorkerRegistration, chave: string) {
  const opcoes: PushSubscriptionOptionsInit = {
    userVisibleOnly: true,
    applicationServerKey: chaveEmBytes(chave),
  }
  try {
    return await registro.pushManager.subscribe(opcoes)
  } catch (erro) {
    const antiga = await registro.pushManager.getSubscription()
    if (!antiga) throw erro
    await antiga.unsubscribe()
    return registro.pushManager.subscribe(opcoes)
  }
}

/**
 * Pede permissão, inscreve o aparelho e conta ao servidor.
 *
 * Devolve o estado do jeito que ficou, inclusive quando a mãe recusou: quem
 * decide o que dizer é a tela, e para isso ela precisa saber se a resposta foi
 * "agora não" ou "nunca".
 */
export async function ligar(): Promise<EstadoDosAvisos> {
  if (!suportado()) return estado()

  const permissao = await Notification.requestPermission()
  if (permissao !== 'granted') return estado()

  const registro = await servicoPronto()
  const { chave } = await api.chavePublicaDePush()
  const inscricao = await assinar(registro, chave)
  const dados = inscricao.toJSON()

  if (!dados.endpoint || !dados.keys?.p256dh || !dados.keys?.auth) {
    throw new Error('o navegador não deu as chaves deste aparelho')
  }

  await api.inscreverNosAvisos({
    endpoint: dados.endpoint,
    keys: { p256dh: dados.keys.p256dh, auth: dados.keys.auth },
  })

  return estado()
}

/**
 * Desliga aqui e no servidor, nessa ordem de importância.
 *
 * Se o servidor não responder, o aparelho sai da inscrição do mesmo jeito: ela
 * pediu silêncio e silêncio é o que ela recebe. A linha que sobra do outro
 * lado se resolve sozinha — a primeira tentativa de entrega volta 410 e o beat
 * a apaga.
 */
export async function desligar(): Promise<EstadoDosAvisos> {
  const registro = await navigator.serviceWorker.getRegistration()
  const inscricao = registro ? await registro.pushManager.getSubscription() : null

  if (inscricao) {
    try {
      await api.sairDosAvisos(inscricao.endpoint)
    } catch {
      // Ver o comentário acima: o 410 limpa o que ficou.
    }
    await inscricao.unsubscribe()
  }

  return estado()
}
